/**
 * Display views (ADR-0115): how the console draws whatever a tool returns — a table, a record
 * card, a map, a timeline, a link network, a form or a document — generically, from the
 * system's own data. A tool action may declare `display` (kind + hints, from the MCP server's
 * `x-ap-display` annotation or the admin's tool setting); otherwise `inferView` picks a view from
 * the shape of the result. Answers reference a tool result (`ResultRef`) instead of retyping it
 * (ADR-0116): the text is the model's, the facts are the system's.
 *
 * This file is the shared contract between the orchestrator (Python twin in
 * `agent_platform_sdk.views`) and the view components. Keep both in step.
 */

export type ViewKind = "table" | "record" | "kv" | "list" | "map" | "timeline" | "network" | "form" | "document" | "chart";

export type ViewTone = "success" | "warning" | "error" | "info" | "default";
export type ViewColumnType = "text" | "number" | "date" | "datetime" | "status" | "entity" | "money" | "link";

export interface ViewHints {
  title?: { en: string; ar: string };
  columns?: { field: string; label?: { en: string; ar: string }; type?: ViewColumnType }[];
  /** Rows/record identify an entity, e.g. {type:"person", key:"emirates_id"}. */
  entity?: { type: string; key: string };
  /** Field -> entity type (e.g. emirates_id -> person). */
  entityFields?: Record<string, { type: string }>;
  status?: { field: string; tones: Record<string, ViewTone> };
  /** Field names, or a GeoJSON field. */
  geo?: { lat: string; lng: string } | { geojson: string };
  time?: { field: string; label?: string; end?: string };
  network?: { nodes: string; edges: string; nodeId?: string; nodeLabel?: string; nodeType?: string; source?: string; target?: string; edgeLabel?: string };
  document?: { url?: string; mime?: string; field?: string };
  /** JSON Schema form whose submit calls a tool action. */
  form?: { schema: Record<string, unknown>; submit: { tool: string; action: string } };
  /** Path to the array inside the result (e.g. "items" or "data.orders"). */
  rows?: string;
  /** Words for a field's codes in each language: `{status: {under_investigation: {en, ar}}}`. */
  valueLabels?: Record<string, Record<string, { en: string; ar: string }>>;
}

export interface ResultRef {
  run_id: string;
  call_id: string;
  tool: string;
  action: string;
}

export interface ViewBlock {
  type: "view";
  kind: ViewKind;
  hints?: ViewHints;
  ref?: ResultRef;
  data?: unknown;
  masked?: boolean;
}

/** What a tool action declares about its display (registry `display`, MCP `x-ap-display`). */
export interface ViewDisplay {
  kind: ViewKind;
  hints?: ViewHints;
}

export const VIEW_KINDS: readonly ViewKind[] = ["table", "record", "kv", "list", "map", "timeline", "network", "form", "document", "chart"];

export function isViewKind(value: unknown): value is ViewKind {
  return typeof value === "string" && (VIEW_KINDS as readonly string[]).includes(value);
}

/** Parse an untrusted object (CUSTOM `view` event value, a rich-reply block) into a ViewBlock. */
export function parseViewBlock(value: unknown): ViewBlock | null {
  if (!isObject(value) || !isViewKind(value.kind)) return null;
  const block: ViewBlock = { type: "view", kind: value.kind };
  if (isObject(value.hints)) block.hints = value.hints as ViewHints;
  const ref = value.ref;
  if (isObject(ref) && typeof ref.run_id === "string" && typeof ref.call_id === "string") {
    block.ref = { run_id: ref.run_id, call_id: ref.call_id, tool: String(ref.tool ?? ""), action: String(ref.action ?? "") };
  }
  if ("data" in value) block.data = value.data;
  if (value.masked === true) block.masked = true;
  return block;
}

// ---------------------------------------------------------------------------- run events + API

/** Where a view's kind and hints came from. */
export type ViewSource = "server" | "admin" | "inferred";

/**
 * CUSTOM `view` (AG-UI): a tool call returned displayable data. Never carries the data itself:
 * fetch it with `GET /v1/runs/{run_id}/results/{call_id}` (`ToolResultView`), which applies the
 * run's access rules.
 */
export interface ViewEvent {
  name: "view";
  node: string;
  kind: ViewKind;
  hints?: ViewHints;
  ref: ResultRef;
  source: ViewSource;
  /** The stored result was cut to the size limit. */
  truncated?: boolean;
  /** Nothing was stored (e.g. too large or not storable): only the kind is known. */
  stored?: boolean;
}

/**
 * CUSTOM `answer_views`: the views an AI step's answer shows (ADR-0116). Attach them to the
 * last assistant message of `node` (TEXT_MESSAGE_START carries `step`). The plain-text version
 * is streamed as its own assistant message flagged `view_fallback` (named here), which rich
 * renderers hide — channels without rich UI say it.
 */
export interface AnswerViewsEvent {
  name: "answer_views";
  node: string;
  views: ViewBlock[];
  fallback?: { en: string; ar: string };
  fallback_message_id?: string;
}

/** `GET /v1/runs/{run_id}/results/{call_id}`. */
export interface ToolResultView {
  ref: ResultRef;
  node: string;
  kind: ViewKind;
  hints?: ViewHints;
  source: ViewSource;
  /** Absent when `masked` and the masking service was unavailable (fail closed). */
  data?: unknown;
  masked: boolean;
  truncated: boolean;
  level?: string | null;
  at?: string | null;
  expires_at?: string | null;
}

export function toolResultPath(ref: Pick<ResultRef, "run_id" | "call_id">): string {
  return `/v1/runs/${encodeURIComponent(ref.run_id)}/results/${encodeURIComponent(ref.call_id)}`;
}

export function isViewEvent(value: unknown): value is ViewEvent {
  return isObject(value) && value.name === "view" && isViewKind(value.kind) && isObject(value.ref);
}

export function isAnswerViewsEvent(value: unknown): value is AnswerViewsEvent {
  return isObject(value) && value.name === "answer_views" && Array.isArray(value.views);
}

/**
 * `[[show:<tool>.<action>]]` / `[[show:<call_id> as table]]`: what a model writes where a result
 * belongs. The orchestrator removes them from the answer and from the stream; this is for text
 * that bypassed it (e.g. history written before ADR-0116).
 */
export const SHOW_MARKER_RE = /\[\[\s*show\s*:\s*([A-Za-z0-9_.:-]{1,128})(?:\s+as\s+([a-z]{2,10}))?\s*\]\]/g;

export function stripShowMarkers(text: string): string {
  return text.replace(SHOW_MARKER_RE, "").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

// ---------------------------------------------------------------------------- path helpers

/** Read a dotted path ("data.orders", "items.0.name") inside a value; "" or undefined = the value. */
export function getPath(value: unknown, path: string | undefined): unknown {
  if (!path) return value;
  let current: unknown = value;
  for (const part of path.split(".")) {
    if (Array.isArray(current) && /^\d+$/.test(part)) current = current[Number(part)];
    else if (isObject(current)) current = current[part];
    else return undefined;
  }
  return current;
}

/** The rows a table/list/timeline/map draws: `hints.rows` inside the result, else the result. */
export function viewRows(data: unknown, hints?: ViewHints): unknown[] {
  const rows = getPath(data, hints?.rows);
  return Array.isArray(rows) ? rows : [];
}

// ---------------------------------------------------------------------------- inference

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/;
const URL_RE = /^https?:\/\/\S+$/i;
const STATUS_NAMES = new Set(["status", "state", "stage", "phase"]);
const MONEY_NAMES = /(^|_)(amount|total|price|cost|fee|balance|value_aed|amount_aed)($|_)/i;
const LAT_NAMES = ["lat", "latitude"];
const LNG_NAMES = ["lng", "lon", "long", "longitude"];
const TIME_NAMES = ["at", "time", "timestamp", "date", "datetime", "occurred_at", "created_at", "when"];
const DOC_MIME_RE = /^(application\/pdf|image\/|text\/plain)/i;

/** The type one column shows, from its name and its values. */
export function inferColumnType(field: string, values: unknown[]): ViewColumnType {
  const present = values.filter((v) => v !== null && v !== undefined && v !== "");
  if (STATUS_NAMES.has(field.toLowerCase())) return "status";
  if (present.length === 0) return "text";
  if (present.every((v) => typeof v === "number")) return MONEY_NAMES.test(field) ? "money" : "number";
  if (present.every((v) => typeof v === "string")) {
    const strings = present as string[];
    if (strings.every((s) => DATE_RE.test(s))) return "date";
    if (strings.every((s) => DATETIME_RE.test(s))) return "datetime";
    if (strings.every((s) => URL_RE.test(s))) return "link";
  }
  return "text";
}

/** Columns for a list of objects: every scalar field seen in the first rows, in first-seen order. */
export function inferColumns(rows: unknown[], limit = 50): NonNullable<ViewHints["columns"]> {
  const sample = rows.slice(0, limit).filter(isObject);
  const fields: string[] = [];
  for (const row of sample) {
    for (const [key, value] of Object.entries(row)) {
      if (!fields.includes(key) && (isScalar(value) || value === null)) fields.push(key);
    }
  }
  return fields.map((field) => ({ field, type: inferColumnType(field, sample.map((r) => r[field])) }));
}

function findKey(obj: Record<string, unknown>, names: string[]): string | undefined {
  const keys = Object.keys(obj);
  for (const name of names) {
    const hit = keys.find((k) => k.toLowerCase() === name);
    if (hit) return hit;
  }
  return undefined;
}

function geoFields(obj: Record<string, unknown>): ViewHints["geo"] | undefined {
  const lat = findKey(obj, LAT_NAMES);
  const lng = findKey(obj, LNG_NAMES);
  if (lat && lng && typeof obj[lat] === "number" && typeof obj[lng] === "number") return { lat, lng };
  for (const [key, value] of Object.entries(obj)) {
    if (isGeoJson(value)) return { geojson: key };
  }
  return undefined;
}

function isGeoJson(value: unknown): boolean {
  return isObject(value) && typeof value.type === "string" && ["FeatureCollection", "Feature", "Point", "Polygon", "LineString", "MultiPoint"].includes(value.type);
}

function documentHints(obj: Record<string, unknown>): ViewHints["document"] | undefined {
  const mime = typeof obj.mime === "string" ? obj.mime : typeof obj.mime_type === "string" ? obj.mime_type : typeof obj.content_type === "string" ? obj.content_type : undefined;
  if (!mime || !DOC_MIME_RE.test(mime)) return undefined;
  const url = typeof obj.url === "string" ? obj.url : undefined;
  const field = ["content_base64", "data", "content", "text"].find((k) => typeof obj[k] === "string");
  if (!url && !field) return undefined;
  return { mime, ...(url ? { url } : {}), ...(field ? { field } : {}) };
}

function timeField(rows: Record<string, unknown>[]): string | undefined {
  const first = rows[0];
  if (!first) return undefined;
  const named = findKey(first, TIME_NAMES);
  const candidates = named ? [named, ...Object.keys(first)] : Object.keys(first);
  return candidates.find((key) => rows.every((r) => typeof r[key] === "string" && (DATE_RE.test(r[key] as string) || DATETIME_RE.test(r[key] as string))));
}

/**
 * The view for a result: the declared display when there is one (its hints completed from the
 * data), else one inferred from the shape. Pure; the orchestrator runs the same rules
 * (`agent_platform_sdk.views.infer_view`) to pick the kind it announces.
 */
export function inferView(data: unknown, declared?: ViewDisplay | null): ViewDisplay {
  if (declared && isViewKind(declared.kind)) {
    const hints: ViewHints = { ...(declared.hints ?? {}) };
    if ((declared.kind === "table" || declared.kind === "timeline") && !hints.columns) {
      const rows = viewRows(data, hints);
      if (rows.length) hints.columns = inferColumns(rows);
    }
    if (declared.kind === "document" && isObject(data)) {
      const found = documentHints(data);
      if (found) hints.document = { ...found, ...(hints.document ?? {}) };
    }
    if (declared.kind === "timeline" && !hints.time) {
      const rows = viewRows(data, hints).filter(isObject);
      const field = timeField(rows);
      if (field) hints.time = { field };
    }
    return { kind: declared.kind, hints };
  }
  return inferShape(data, "");
}

function inferShape(data: unknown, path: string): ViewDisplay {
  const rowsHint = path ? { rows: path } : {};
  if (typeof data === "string") {
    if (/^data:(application\/pdf|image\/[a-z+.-]+);base64,/i.test(data)) {
      const mime = data.slice(5, data.indexOf(";"));
      return { kind: "document", hints: { document: { mime } } };
    }
    return { kind: "kv" };
  }
  if (Array.isArray(data)) {
    if (data.length > 0 && data.every(isObject)) {
      const rows = data as Record<string, unknown>[];
      const first = rows[0] as Record<string, unknown>;
      const geo = geoFields(first);
      if (geo && rows.every((r) => geoFields(r))) return { kind: "map", hints: { ...rowsHint, geo } };
      return { kind: "table", hints: { ...rowsHint, columns: inferColumns(rows) } };
    }
    return { kind: "list", hints: rowsHint };
  }
  if (!isObject(data)) return { kind: "kv" };
  if (isGeoJson(data)) return { kind: "map", hints: { geo: { geojson: path } } };
  if (Array.isArray(data.nodes) && Array.isArray(data.edges)) {
    const prefix = path ? `${path}.` : "";
    return { kind: "network", hints: { network: { nodes: `${prefix}nodes`, edges: `${prefix}edges` } } };
  }
  const doc = documentHints(data);
  if (doc) return { kind: "document", hints: { document: doc } };
  // A paged or wrapped list ({items,total}, {data:[...]}, {results:[...]}): draw the list.
  // `structured`: a third-party MCP server's result behind the connector host ({text, structured}).
  for (const key of ["items", "results", "result", "rows", "records", "data", "structured"]) {
    const inner = data[key];
    if (Array.isArray(inner) && inner.length > 0 && inner.every(isObject)) return inferShape(inner, path ? `${path}.${key}` : key);
    if ((key === "data" || key === "structured") && isObject(inner)) {
      const nested = inferShape(inner, path ? `${path}.${key}` : key);
      if (nested.kind !== "kv" && nested.kind !== "record") return nested;
    }
  }
  const geo = geoFields(data);
  if (geo) return { kind: "map", hints: { geo } };
  const values = Object.values(data);
  return { kind: values.every((v) => isScalar(v) || v === null) ? "kv" : "record" };
}

// ---------------------------------------------------------------------------- plain text

/** A short plain-text version for channels without rich UI (ADR-0116): first rows of a table, key: value lines. */
export function viewPlainText(data: unknown, view: ViewDisplay, lang: "en" | "ar" = "en", maxRows = 5): string {
  const hints = view.hints ?? {};
  const title = hints.title ? `${hints.title[lang]}\n` : "";
  if (view.kind === "table" || view.kind === "list" || view.kind === "timeline" || view.kind === "map") {
    const rows = Array.isArray(data) && !hints.rows ? data : viewRows(data, hints);
    const columns = (hints.columns ?? inferColumns(rows)).slice(0, 4);
    const lines = rows.slice(0, maxRows).map((row) => {
      if (!isObject(row)) return `- ${String(row)}`;
      return `- ${columns.map((c) => formatScalar(row[c.field])).filter(Boolean).join(" | ")}`;
    });
    const more = rows.length > maxRows ? `\n(+${rows.length - maxRows})` : "";
    return `${title}${lines.join("\n")}${more}`.trim();
  }
  if (isObject(data)) {
    const lines = Object.entries(data)
      .filter(([, v]) => isScalar(v))
      .slice(0, 8)
      .map(([k, v]) => `${k}: ${formatScalar(v)}`);
    return `${title}${lines.join("\n")}`.trim();
  }
  return `${title}${formatScalar(data)}`.trim();
}

function formatScalar(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return "";
  return String(value);
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isScalar(value: unknown): boolean {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}
